// ─────────────────────────────────────────────────────────────
// PythonTab — code editor workspace.
//
// Left panel (collapsible):
//   Expanded (280px): script controls, Code|Chat toggle, image section
//   Collapsed (60px): icon-only strip with stacked Code|Chat icons
//
// Code mode:  [LeftPanel] | [Toolbar(Copy/Download) + Monaco] | [AiPanel sidebar]
// Chat mode:  [LeftPanel] | [AiPanel full-width]
//
// The Code|Chat toggle lives in the left sidebar (above the image area
// when expanded; as stacked icons above Library/Upload when collapsed).
// Panel collapse/expand is fully independent of editor mode.
//
// editorMode is received as props from ScriptHelperApp, which resets
// it to "code" when the user navigates away from the Editor tab.
// ─────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from "react";
import Editor, { OnMount } from "@monaco-editor/react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CloudUploadConfirmDialog } from "./CloudUploadConfirmDialog";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SaveAsOutlinedIcon from "@mui/icons-material/SaveAsOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { AiPanel } from "./AiPanel";
import { ConsolePanel } from "./ConsolePanel";
import { MetadataDetailsPanel } from "./MetadataChip";
import {
  FullscreenImageModal,
  LibrarySelectModal,
  UploadImageModal,
  SaveScriptModal,
  SaveVersionDialog,
} from "./modals";
import type { ChatMessage, EditorMode, PlaceholderImage, ScriptVersion } from "./types";
import { imageApi, userScriptsApi } from "../../api/client";
import type { ExecutionResult } from "../../api/client";

// ── Download filename helper ──────────────────────────────────
/**
 * Build a sensible download filename for the Python tab's Download button.
 *
 * Examples:
 *   "Gaussian Blur"            → "gaussian_blur.py"
 *   "Spermatid Morphometry"    → "spermatid_morphometry.py"
 *   "Edge Detection (Sobel)"   → "edge_detection_sobel.py"
 *   "My Cool Script v2!"       → "my_cool_script_v2.py"
 *   "  Leading & Trailing  "   → "leading_trailing.py"
 *   ""  or  null  or  whitespace-only  → "untitled_script.py"
 *   "!!! @@@ ###"  (no alphanumerics) → "untitled_script.py"
 *
 * ASCII-only by design — non-ASCII characters (em-dashes, accents, non-Latin
 * scripts) collapse to underscores. Keeps the filename portable across OSes
 * without surprising encoding issues.
 */
function buildDownloadFilename(title: string | null | undefined): string {
  if (!title) return "untitled_script.py";
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")   // any run of non-alphanumerics → single underscore
    .replace(/^_+|_+$/g, "");       // strip leading + trailing underscores
  return slug ? `${slug}.py` : "untitled_script.py";
}

// ── Auth helper ───────────────────────────────────────────────
function getLoggedInName(): string {
  try {
    const raw = localStorage.getItem("auth_user");
    const user = raw ? JSON.parse(raw) : null;
    if (user?.full_name) return user.full_name as string;
    if (user?.email) return user.email as string;
  } catch { /* ignore */ }
  return "Unknown";
}

// ── Maps "Default parameters" block — parse / build / sync ───
//
// The canonical Maps Studio format (used by the real Thermo Fisher demos):
//
//   # Default parameters
//   #{
//   #"RunMode" : "manual",
//   #"ScriptMode" : "singletiles",
//   #"ScriptParameters" : "brightness=1.0;invert=false"
//   #}
//   # Default parameters end
//
// The Helper reads the block on script load, extracts ScriptParameters,
// and splits on ";" to populate the parameter panel. When the user edits
// the panel we rewrite just the ScriptParameters value inside the block,
// preserving RunMode/ScriptMode/PrepareImages exactly as Maps requires.

const DEFAULT_PARAMS_BLOCK_RE = /# Default parameters\n((?:#[^\n]*\n)+)# Default parameters end/;

// Parse the whole block into a JSON object (by stripping the leading '#'
// from each body line and letting JSON.parse do the rest).
function parseMapsBlock(code: string): {
  match: RegExpMatchArray;
  data: Record<string, unknown>;
} | null {
  const match = code.match(DEFAULT_PARAMS_BLOCK_RE);
  if (!match) return null;
  const jsonBody = match[1]
    .split("\n")
    .map((line) => line.replace(/^#/, ""))
    .join("\n")
    .trim();
  try {
    const data = JSON.parse(jsonBody);
    if (data && typeof data === "object" && !Array.isArray(data)) {
      return { match, data: data as Record<string, unknown> };
    }
  } catch {
    // Malformed block — degrade gracefully
  }
  return null;
}

// Serialise a data object back into the #-prefixed JSON-in-comments format
// that Maps Studio expects.
function buildMapsBlock(data: Record<string, unknown>): string {
  const entries = Object.entries(data);
  const body = entries.map(([k, v], i) => {
    const comma = i < entries.length - 1 ? "," : "";
    return `#"${k}" : ${JSON.stringify(v)}${comma}`;
  });
  return ["# Default parameters", "#{", ...body, "#}", "# Default parameters end"].join("\n");
}

// Extract the ScriptParameters string, split on ";" into panel rows.
// Returns null if the block is missing, ScriptParameters is absent/empty,
// or the value doesn't look like key=value pairs.
function parseDefaultParameters(code: string): Array<{ id: number; key: string; value: string }> | null {
  const parsed = parseMapsBlock(code);
  if (!parsed) return null;
  const sp = parsed.data["ScriptParameters"];
  if (typeof sp !== "string" || !sp.trim() || !sp.includes("=")) return null;
  let id = 0;
  const pairs = sp
    .split(";")
    .map((p) => p.trim())
    .filter((p) => p.includes("="))
    .map((p) => {
      const eq = p.indexOf("=");
      return { id: ++id, key: p.slice(0, eq).trim(), value: p.slice(eq + 1).trim() };
    });
  return pairs.length > 0 ? pairs : null;
}

// Write panel pairs back into the ScriptParameters field of the block.
// Preserves RunMode/ScriptMode/PrepareImages untouched. If the block is
// missing, creates a minimal block with a sensible default (singletiles).
function syncParamsToCode(currentCode: string, pairs: Array<{ key: string; value: string }>): string {
  const valid = pairs.filter((p) => p.key.trim());
  const paramString = valid.map((p) => `${p.key.trim()}=${p.value}`).join(";");
  const parsed = parseMapsBlock(currentCode);
  if (parsed) {
    const newData: Record<string, unknown> = { ...parsed.data, ScriptParameters: paramString };
    const newBlock = buildMapsBlock(newData);
    return currentCode.replace(parsed.match[0], newBlock);
  }
  // No block present — only create one if the user has actually added params
  if (valid.length === 0) return currentCode;
  const newBlock = buildMapsBlock({
    RunMode: "manual",
    ScriptMode: "singletiles",
    ScriptParameters: paramString,
  });
  return newBlock + "\n\n" + currentCode.replace(/^\n+/, "");
}

// ── Persistent-version display helpers ────────────────────────
// Compact YYYY-MM-DD for the version dropdown — same format used
// elsewhere in the UI for "saved at" timestamps. Keeps the right
// column of each menu row narrow so notes have room to breathe.
function formatVersionDate(iso: string): string {
  // Trim to date part — backend ships ISO 8601 timestamps. If parsing
  // fails (defensive — backend should always be valid), show whatever
  // came in rather than crashing.
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : iso;
}

// ── Version header helpers ────────────────────────────────────
function formatInjectionTimestamp(date: Date): string {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const rawHours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = rawHours >= 12 ? "PM" : "AM";
  const hours = rawHours % 12 || 12;
  return `${month} ${day}, ${year} at ${hours}:${minutes} ${ampm}`;
}

function buildVersionHeader(incomingCode: string, existingCode: string, modifiedBy: string): string {
  const headerLineRegex = /^#\s*v(\d+)\.(\d+)(?:\s*\|[^\n]*)?\n?/im;

  let major = 1;
  let minor = 0;

  const existingMatch = existingCode.match(headerLineRegex);
  if (existingMatch) {
    major = parseInt(existingMatch[1], 10);
    minor = parseInt(existingMatch[2], 10) + 1;
  }

  const timestamp = formatInjectionTimestamp(new Date());
  const newHeader = `# v${major}.${minor} | ${modifiedBy} | ${timestamp}`;

  // Strip any existing version header from the incoming code before prepending
  const stripped = incomingCode.replace(/^#\s*v\d+\.\d+(?:\s*\|[^\n]*)?\n?/im, "");

  return `${newHeader}\n${stripped}`;
}

// ── Layout constants ──────────────────────────────────────────
const PANEL_WIDTH = 280;
const PANEL_COLLAPSED_WIDTH = 60;

// ── Scaffold data ─────────────────────────────────────────────
const INITIAL_CODE = `# Default parameters
#{
#"RunMode" : "manual",
#"ScriptMode" : "singletiles",
#"ScriptParameters" : ""
#}
# Default parameters end

import os
import MapsBridge

# New script — ask Marie to write this for you, or start typing.
# Every Maps script reads input via MapsBridge, processes it, and sends
# output back via MapsBridge.send_single_tile_output (tile set scripts)
# or MapsBridge.create_image_layer (image layer scripts).
#
# Runtime parameters come in as request.script_parameters (raw string).
# The ScriptParameters value in the block above is the default Maps uses.

def main():
    request = MapsBridge.ScriptTileSetRequest.from_stdin()
    # TODO: your processing here

if __name__ == "__main__":
    main()
`;

// ── Props ─────────────────────────────────────────────────────
interface PythonTabProps {
  containerWidth: number;
  colorMode: "light" | "dark";
  editorMode: EditorMode;
  onEditorModeChange: (mode: EditorMode) => void;
  /** Chat history lifted to ScriptHelperApp so it persists across tab switches */
  chatMessages: ChatMessage[];
  onChatMessagesChange: (messages: ChatMessage[]) => void;
  /** Called once on editor mount with a ref to the Monaco editor instance */
  onEditorReady?: (editorRef: React.MutableRefObject<any>) => void;
  /** Called whenever the editor code changes */
  onCodeChange?: (code: string) => void;
  /** Called when the user clicks Run — receives current code, optional image, and a key=value;key=value parameter string */
  onRun?: (scriptCode: string, imageId?: number, imageFile?: File, parameters?: string) => void;
  /** True while an execution request is in-flight */
  isRunning?: boolean;
  /** When set, pre-selects this library image id. Parent clears it via onPreloadImageConsumed. */
  preloadImageId?: number | null;
  /** Called after PythonTab has consumed preloadImageId so the parent can reset the signal. */
  onPreloadImageConsumed?: () => void;
  /** Code to populate the editor with on mount (e.g. after loading a starter script). */
  initialCode?: string;
  /**
   * Incremented by the parent each time an external script is loaded into the editor
   * (e.g. from Starter Scripts or My Scripts). PythonTab uses this to update currentScriptId.
   */
  scriptLoadKey?: number;
  /**
   * The database id of the script most recently loaded by the parent, or null if a
   * starter/new script was loaded. PythonTab reads this when scriptLoadKey fires to
   * decide whether Save should overwrite an existing record or prompt for a name.
   */
  loadedScriptId?: number | null;
  /**
   * The display title of the script most recently loaded into the editor, or
   * null if the script is new/untitled. Used to derive a meaningful filename
   * on Download.
   */
  loadedScriptTitle?: string | null;
  /**
   * When non-null, automatically fires this text into Marie's chat for error analysis.
   * Parent resets to null via onAutoAnalyzeErrorConsumed after it has been sent.
   */
  autoAnalyzeError?: string | null;
  onAutoAnalyzeErrorConsumed?: () => void;
  /** Increment to trigger a flash on the image placeholder (no image loaded on Run). */
  imageFlashKey?: number;
  /** When non-null, auto-sends this prompt to Marie as a visible user message. */
  pendingPrompt?: string | null;
  onPendingPromptConsumed?: () => void;
  /** Called when the user clicks New Script — parent resets code, scriptId, and Marie's chat. */
  onNewScript?: () => void;
  /**
   * Bumped by the parent when the workspace should be wiped (e.g. New Chat
   * in the AI panel). PythonTab listens via useEffect and clears its
   * locally-owned image selection (selectedImage / tempImageFile). The
   * parent already handles editor content + script id + chat history reset.
   */
  workspaceClearKey?: number;
  /**
   * Forwarded to AiPanel — called by "Start new chat" so the parent can
   * trigger a full workspace reset (editor + image + scriptId + chat +
   * session) on top of the local chat reset AiPanel already does.
   */
  onClearWorkspace?: () => void;
  /**
   * Called when a hero-state chip in AiPanel's Chat mode should load a starter
   * script into the editor. Threaded straight through to AiPanel so the chip's
   * "I've loaded X…" acknowledgement path reuses the existing ScriptHelperApp
   * handler rather than reinventing it.
   */
  onLoadScript?: (
    code: string,
    defaultImageId?: number | null,
    savedScriptId?: number | null,
    scriptTitle?: string,
  ) => void;
  /** Active Marie session id — sent on every save so saved scripts join the session capture. */
  marieSessionId?: number | null;
  /** Setter — forwarded to AiPanel so it can capture session id from the meta frame. */
  onMarieSessionIdChange?: (id: number | null) => void;
  /** Bumped by parent when a fresh Marie session should start; passed through to AiPanel. */
  newSessionArmKey?: number;
  /** Re-arm the session — forwarded to AiPanel for its New Chat handler. */
  onArmNewSession?: () => void;
  /**
   * Most recent execution result — drives the bottom console panel that
   * mirrors the Output tab's console. Lets users see the same
   * stdout/stderr Marie sees when she auto-analyses a failure, without
   * leaving the editor view. Null = no run yet (panel shows placeholder).
   */
  lastResult?: ExecutionResult | null;
  /**
   * Per-user Marie model allowlist (Sam, 2026-05-14). Forwarded to both
   * AiPanel instances (Chat-mode and sidebar). See AiPanel.AiPanelProps
   * for the filter semantics.
   */
  allowedModels?: string[] | null;
  /**
   * Tier-limit toast hook forwarded to AiPanel. Parent (ScriptHelperApp)
   * owns the Snackbar to match the "no image selected" toast positioning.
   */
  onTierLimitToast?: (info: {
    kind: "model_not_allowed" | "quota_exceeded_marie_chat";
    message: string;
  }) => void;
  /**
   * Lifted Marie model selection (Greg, 2026-05-15). Owned by
   * ScriptHelperApp so both AiPanel instances here (Code-mode sidebar
   * and Chat-mode full-width) share one value with the OutputTab panel.
   * Auto-switching to the Output tab after a script run no longer resets
   * the picker back to the Opus 4.7 default.
   */
  selectedMarieModel: string;
  onSelectedMarieModelChange: (modelId: string) => void;
}

// ── Component ─────────────────────────────────────────────────
export function PythonTab({ containerWidth, colorMode, editorMode, onEditorModeChange, chatMessages, onChatMessagesChange, onEditorReady, onCodeChange, onRun, isRunning = false, preloadImageId, onPreloadImageConsumed, initialCode, scriptLoadKey, loadedScriptId, loadedScriptTitle, workspaceClearKey, autoAnalyzeError, onAutoAnalyzeErrorConsumed, imageFlashKey, pendingPrompt, onPendingPromptConsumed, onNewScript, onLoadScript, onClearWorkspace, marieSessionId, onMarieSessionIdChange, newSessionArmKey, onArmNewSession, lastResult, allowedModels, onTierLimitToast, selectedMarieModel, onSelectedMarieModelChange }: PythonTabProps) {
  // Panel collapse — fully user-controlled, independent of editor mode
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  // Flash state for the image placeholder — triggered when Run is clicked with no image
  const [imagePlaceholderFlashing, setImagePlaceholderFlashing] = useState(false);
  const prevImageFlashKey = useRef<number>(imageFlashKey ?? 0);
  useEffect(() => {
    if (imageFlashKey === undefined) return;
    if (imageFlashKey === prevImageFlashKey.current) return;
    prevImageFlashKey.current = imageFlashKey;
    // Expand panel so user can see the flash
    setPanelCollapsed(false);
    setImagePlaceholderFlashing(true);
    const t = setTimeout(() => setImagePlaceholderFlashing(false), 2000);
    return () => clearTimeout(t);
  }, [imageFlashKey]);

  const [selectedImage, setSelectedImage] = useState<PlaceholderImage | null>(null);
  const [selectedImageBlobUrl, setSelectedImageBlobUrl] = useState<string | null>(null);
  // Temp file chosen directly from disk — never uploaded to library
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);
  const tempFileInputRef = useRef<HTMLInputElement>(null);
  // Friendly cloud-OK confirmation gate for the temp-image picker. The picked
  // file is held here until the user confirms; "Not now" discards it.
  const [pendingTempFile, setPendingTempFile] = useState<File | null>(null);

  // ── Proactive auto-image-analysis trigger (Mia, 2026-06-05) ─────
  // Bumped ONLY by the two USER-INITIATED image-set paths (picking a library
  // image, confirming a temp upload). Drives AiPanel's autoAnalyzeImageKey so
  // Marie posts a short description automatically. Deliberately NOT bumped from
  // the preloadImageId effect (starter-script auto-pairing) — that path sets
  // selectedImage programmatically and must stay silent. The identity string
  // (library id, or temp file name+size) lets AiPanel dedupe re-fires for the
  // same image; setImageAnalysisKey batches in the SAME render as the
  // setSelectedImage/setTempImageFile call, so by the time AiPanel's effect
  // runs, the getters read the fresh selection.
  const [imageAnalysisKey, setImageAnalysisKey] = useState(0);
  const [imageAnalysisIdentity, setImageAnalysisIdentity] = useState<string | null>(null);
  function triggerImageAnalysis(identity: string) {
    setImageAnalysisIdentity(identity);
    setImageAnalysisKey((k) => k + 1);
  }

  // Fetch blob URL for library images
  useEffect(() => {
    if (!selectedImage) { setSelectedImageBlobUrl(null); return; }
    const id = parseInt(selectedImage.id, 10);
    if (isNaN(id)) { setSelectedImageBlobUrl(null); return; }
    let cancelled = false;
    // Editor's selected-image preview is a 140px-tall sidebar tile — fetch the
    // 400px cached thumbnail (~100 KB), not the full TIFF (can be 150 MB+).
    imageApi.getThumbnailUrl(id).then((url) => {
      if (!cancelled) setSelectedImageBlobUrl(url);
    }).catch(() => { if (!cancelled) setSelectedImageBlobUrl(null); });
    return () => { cancelled = true; };
  }, [selectedImage]);

  // When a starter script specifies a paired image, pre-select it.
  // The blob URL effect above handles fetching the actual image bytes once selectedImage is set.
  useEffect(() => {
    if (!preloadImageId) return;
    // Starter scripts reference shared library images via default_image_id, so
    // any auto-preload here is treated as "library" for the Marie vision-attach
    // source field. If a user-owned image is ever needed via this path, the
    // upstream loader should pass an explicit source.
    setSelectedImage({
      id: String(preloadImageId),
      name: "Library Image",
      description: "",
      type: "SEM",
      source: "library",
    });
    // Mirror onto the auto-pair tracker so the first Save after a starter/
    // Community load sees the existing pairing and skips the redundant toast.
    setCurrentScriptDefaultImageId(preloadImageId);
    onPreloadImageConsumed?.();
  }, [preloadImageId]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleTempFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset input immediately so picking the same file again re-triggers
    // onChange — both when we proceed and when the user later hits "Not now".
    e.target.value = "";
    if (!file) return;
    // Don't accept the file yet — show the cloud-OK confirmation first.
    setPendingTempFile(file);
  }

  // User confirmed the temp image is OK to upload — accept it exactly as the
  // original flow did.
  function confirmTempFile() {
    const file = pendingTempFile;
    setPendingTempFile(null);
    if (!file) return;
    // Revoke any previous temp blob
    if (tempImageFile && selectedImageBlobUrl && !selectedImage) URL.revokeObjectURL(selectedImageBlobUrl);
    setTempImageFile(file);
    setSelectedImage(null);  // clear any library selection
    setSelectedImageBlobUrl(URL.createObjectURL(file));
    // User-initiated upload → ask Marie to describe it. Identity = name+size so
    // re-uploading the same file (a fresh File instance) doesn't re-fire.
    triggerImageAnalysis(`temp:${file.name}:${file.size}`);
  }

  // User backed out — discard the pending file. The input was already reset
  // in handleTempFileSelect, so the same file can be re-picked.
  function cancelTempFile() {
    setPendingTempFile(null);
  }

  function clearImage() {
    if (tempImageFile && selectedImageBlobUrl) URL.revokeObjectURL(selectedImageBlobUrl);
    setTempImageFile(null);
    setSelectedImage(null);
    setSelectedImageBlobUrl(null);
  }

  // ── Auto-pair-on-Save helper (Greg, 2026-05-14) ─────────────────
  // Returns the persistent image id ONLY when the editor's current selection
  // is a library or user image (i.e. has a real DB id). Returns null for
  // temp disk uploads (no persistent id) and when nothing is selected.
  // Save handlers feed this into the `default_image_id` decision.
  function selectedLibraryImageId(): number | null {
    if (tempImageFile) return null;          // temp uploads have no persistent id
    if (!selectedImage) return null;          // nothing selected
    const n = parseInt(selectedImage.id, 10);
    return Number.isNaN(n) ? null : n;
  }

  // When the parent signals a full workspace reset (New Chat / New Script),
  // wipe the locally-owned state: image selection, parameter panel, and the
  // local `code` mirror (otherwise switching to Chat-mode-then-back would
  // re-render the stale code when the editor remounts, since Monaco's onChange
  // doesn't fire from the parent's imperative setValue("") when the editor
  // is unmounted in Chat mode).
  //
  // Other reset bits (loadedScriptId, chat history, Marie session, Monaco
  // editor content via setValue) live in the parent and are already handled
  // there.
  //
  // Guarded with a "prev" ref so it doesn't fire on initial mount (when
  // workspaceClearKey is its initial value).
  const prevWorkspaceClearKey = useRef<number | undefined>(workspaceClearKey);
  useEffect(() => {
    if (workspaceClearKey === undefined) return;
    if (workspaceClearKey === prevWorkspaceClearKey.current) return;
    prevWorkspaceClearKey.current = workspaceClearKey;
    // If a preload is incoming on this same render, the preloadImageId effect
    // above has already set selectedImage to the paired library image. React
    // runs effects in declaration order, so without this guard we'd wipe the
    // image the preload effect just set. The onPreloadImageConsumed callback
    // queues preloadImageId=null for the NEXT render, so the closure here
    // still sees the truthy value — that's the signal we use.
    //
    // In the preload case we still clear any temp-upload state, because a
    // preload always means "switch from whatever to this library image" and
    // a lingering temp file would conflict. The preloadImageId effect handles
    // setCurrentScriptDefaultImageId, so we don't null that here either.
    if (preloadImageId) {
      if (tempImageFile && selectedImageBlobUrl) URL.revokeObjectURL(selectedImageBlobUrl);
      setTempImageFile(null);
    } else {
      clearImage();
      // Reset the auto-pair tracker alongside the image clear — workspace reset
      // means "we're done with that script", so its remembered pairing is no
      // longer relevant for the next save.
      setCurrentScriptDefaultImageId(null);
    }
    // Reset the local code mirror to empty (matches parent's setValue("") in
    // resetWorkspace — and matches the existing New Script button behavior).
    // Belt-and-braces the imperative setValue: the parent already attempted it,
    // but that's a no-op when the editor is unmounted in Chat mode. Without
    // this, switching Chat→Code after a reset would re-render the prior code
    // from the stale local `code` state.
    setCode("");
    if (editorRef.current) editorRef.current.setValue("");
    onCodeChange?.("");
    // Parameter panel reflects the loaded script's # Default parameters block —
    // clear it so the empty editor doesn't show params from the prior script.
    setParamPairs([]);
    setShowParamPanel(false);
  }, [workspaceClearKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [aiPanelWidth, setAiPanelWidth] = useState(360);

  // Editor content — seeded from initialCode when provided (e.g. after loading a starter script)
  const [code, setCode] = useState(initialCode ?? INITIAL_CODE);

  // ── Script parameters (key=value pairs, matching real Maps format) ──────
  const _initialParsed = parseDefaultParameters(initialCode ?? INITIAL_CODE);
  const paramIdCounter = useRef(_initialParsed ? _initialParsed.length : 0);
  const [paramPairs, setParamPairs] = useState<Array<{ id: number; key: string; value: string }>>(
    _initialParsed ?? []
  );
  const [showParamPanel, setShowParamPanel] = useState(_initialParsed !== null && _initialParsed.length > 0);

  // Sync paramPairs → script code whenever the user edits the panel.
  // Runs after every paramPairs change except the initial mount.
  const isFirstParamSync = useRef(true);
  useEffect(() => {
    if (isFirstParamSync.current) { isFirstParamSync.current = false; return; }
    const currentCode = editorRef.current?.getValue() ?? code;
    const newCode = syncParamsToCode(currentCode, paramPairs);
    if (newCode === currentCode) return;
    setCode(newCode);
    onCodeChange?.(newCode);
    if (editorRef.current) editorRef.current.setValue(newCode);
  }, [paramPairs]); // eslint-disable-line react-hooks/exhaustive-deps

  function addParamPair() {
    setParamPairs((prev) => [...prev, { id: ++paramIdCounter.current, key: "", value: "" }]);
  }
  function updateParamPair(id: number, field: "key" | "value", val: string) {
    setParamPairs((prev) => prev.map((p) => p.id === id ? { ...p, [field]: val } : p));
  }
  function removeParamPair(id: number) {
    setParamPairs((prev) => prev.filter((p) => p.id !== id));
  }
  function buildParamString(): string | undefined {
    const valid = paramPairs.filter((p) => p.key.trim());
    if (valid.length === 0) return undefined;
    return valid.map((p) => `${p.key.trim()}=${p.value}`).join(";");
  }

  // Tracks the saved script id for the current editor session.
  // null means the script has never been saved (or a new external script was loaded).
  const [currentScriptId, setCurrentScriptId] = useState<number | null>(null);

  // Tracks the loaded script's currently-known `default_image_id`. Drives the
  // "auto-pair on Save" logic (Greg, 2026-05-14): the editor's selected image
  // is silently promoted to the script's default_image_id on every Save, and
  // a Snackbar acknowledges the pairing only when the value actually changed.
  //
  // Initial-value sources:
  //   • Starter / Community loads → seeded from `preloadImageId` (same id the
  //     parent passes for auto-preselecting the image), so the equality check
  //     skips the redundant "Paired with X" toast on the first Save after load.
  //   • My Scripts loads → starts null (the parent intentionally omits the
  //     image id to avoid auto-preselecting). The first Save after the user
  //     picks an image will show the pairing toast, even if they happened to
  //     re-select the same image that was already paired. Tolerable trade-off
  //     for keeping the data flow simple — known one-time noise per load.
  //
  // Refresh sources:
  //   • Every PUT /api/scripts/:id response (overwrite-head, restore, follow-up
  //     image PUT) carries the authoritative `default_image_id` — mirror it.
  //   • POST /api/scripts/:id/versions returns the parent script in `result.script`
  //     — mirror that too in case the backend updated anything else.
  //   • workspaceClearKey reset → null.
  const [currentScriptDefaultImageId, setCurrentScriptDefaultImageId] =
    useState<number | null>(null);

  // ── Persistent versioning state ────────────────────────────
  // Versions list — fetched on script load, refetched after any version
  // mutation (save-as-new, restore, notes edit). Sorted DESC by version_number
  // (matches the backend response). Empty array when no script is loaded
  // or the listing fetch failed (versionsError is the diagnostic).
  const [versions, setVersions] = useState<ScriptVersion[]>([]);
  const [headVersionId, setHeadVersionId] = useState<number | null>(null);
  const [publishedVersionId, setPublishedVersionId] = useState<number | null>(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);

  // Which version is currently mounted in the editor.
  // null = "viewing head" (the default after script load and after restore).
  // Set to a non-null version id when the user picks a non-head row from
  // the dropdown. The "viewing not current" indicator and Restore button
  // both key off whether this differs from headVersionId.
  const [viewingVersionId, setViewingVersionId] = useState<number | null>(null);

  // Anchor for the version-picker Menu (rendered when the dropdown chip
  // is clicked). Null = closed.
  const [versionMenuAnchor, setVersionMenuAnchor] = useState<HTMLButtonElement | null>(null);

  // Anchor for the sidebar New Script split-button's chevron menu — opens
  // a LOAD-direction version picker so the user can pull any saved version
  // into the editor without leaving the script-controls column. Mirrors
  // the editor-toolbar dropdown (same data, same handlePickVersion wiring,
  // so the toolbar's "viewing not current" indicator stays in lockstep).
  // Null = closed.
  const [newScriptMenuAnchor, setNewScriptMenuAnchor] = useState<HTMLElement | null>(null);

  // Save dialog — radio-card chooser opened when Save is clicked on an
  // already-saved script. Lets the user pick "overwrite head" vs. "create
  // new version" inline (with the notes field enabled only for the latter).
  // For new/unsaved scripts, Save still routes to SaveScriptModal instead.
  const [showSaveVersionDialog, setShowSaveVersionDialog] = useState(false);
  const [savingVersion, setSavingVersion] = useState(false);

  // Restore-confirmation dialog state. pendingRestore holds the version row
  // the user clicked "Restore as current" against; the dialog calls the API
  // when confirmed.
  const [pendingRestore, setPendingRestore] = useState<ScriptVersion | null>(null);
  const [restoring, setRestoring] = useState(false);

  // Save operation feedback
  const [saveSnackbar, setSaveSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  // ── Versions fetch / refetch ────────────────────────────────
  // Whenever the loaded script changes (or after a successful save/restore),
  // refetch the versions list so the dropdown is in sync with the backend.
  // versionsRefreshKey is bumped after every mutation to force a refetch
  // without changing currentScriptId.
  const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);

  useEffect(() => {
    if (currentScriptId === null) {
      // No saved script loaded — clear everything so the dropdown stays hidden.
      setVersions([]);
      setHeadVersionId(null);
      setPublishedVersionId(null);
      setViewingVersionId(null);
      setVersionsError(null);
      return;
    }
    let cancelled = false;
    setVersionsLoading(true);
    setVersionsError(null);
    userScriptsApi
      .listVersions(currentScriptId)
      .then((res) => {
        if (cancelled) return;
        setVersions(res.versions);
        setHeadVersionId(res.head_version_id);
        setPublishedVersionId(res.published_version_id);
        // Fresh script load → snap viewingVersionId back to "head" so the
        // dropdown shows "current" again. After a refetch caused by a save,
        // viewingVersionId stays null (the user is still viewing whatever
        // they just saved, which is now the new head).
        setViewingVersionId(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setVersionsError(err instanceof Error ? err.message : "Failed to load versions.");
        setVersions([]);
        setHeadVersionId(null);
        setPublishedVersionId(null);
      })
      .finally(() => {
        if (!cancelled) setVersionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [currentScriptId, versionsRefreshKey]);

  // ── Derived: current head version, "viewing version", chip label ──
  const headVersion: ScriptVersion | null = useMemo(
    () => versions.find((v) => v.id === headVersionId) ?? null,
    [versions, headVersionId],
  );
  const viewingVersion: ScriptVersion | null = useMemo(
    () => (viewingVersionId === null
      ? headVersion
      : (versions.find((v) => v.id === viewingVersionId) ?? null)),
    [versions, viewingVersionId, headVersion],
  );
  const isViewingHead = viewingVersionId === null
    || (headVersion !== null && viewingVersionId === headVersion.id);

  // When the parent loads a new external script, sync both the editor content
  // and currentScriptId. Critical for Chat mode, where Monaco is unmounted and
  // ScriptHelperApp's setValue() call no-ops (the ref is null). Without this,
  // switching back to Code mode shows the previous (or default) script.
  // - My Scripts: loadedScriptId is the real DB id → Save will overwrite silently.
  // - Starter Scripts / new: loadedScriptId is null → Save will prompt for a name.
  useEffect(() => {
    if (scriptLoadKey !== undefined) {
      setCurrentScriptId(loadedScriptId ?? null);
      // Reset the auto-pair tracker on every script load. For starter / Community
      // loads, the preloadImageId effect re-seeds it. For My Scripts loads the
      // parent intentionally passes no preload, so we stay at null and the first
      // Save with an image selected fires the pairing toast (see state comment).
      setCurrentScriptDefaultImageId(null);
      if (initialCode !== undefined) {
        setCode(initialCode);
        if (editorRef.current) {
          editorRef.current.setValue(initialCode);
        }
        onCodeChange?.(initialCode);
        // Auto-populate parameter panel from the MAPS PARAMETERS AND DEFAULT block
        const parsed = parseDefaultParameters(initialCode);
        if (parsed && parsed.length > 0) {
          paramIdCounter.current = parsed.length;
          setParamPairs(parsed);
          setShowParamPanel(true);
        } else {
          setParamPairs([]);
          setShowParamPanel(false);
        }
      }
    }
  }, [scriptLoadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Monaco editor instance ref — exposed to parent via onEditorReady
  const editorRef = useRef<any>(null);
  // Queues code injected while the editor is unmounted (chat mode).
  // Applied immediately when the editor next mounts.
  const pendingInjectionRef = useRef<string | null>(null);

  const handleEditorMount: OnMount = (editor) => {
    editorRef.current = editor;
    onEditorReady?.(editorRef);
    // Apply any code that was injected while the editor was unmounted (chat mode)
    if (pendingInjectionRef.current !== null) {
      editor.setValue(pendingInjectionRef.current);
      pendingInjectionRef.current = null;
    }
    // Clear the ref when the editor is disposed (e.g. switching to Chat mode).
    // Without this, editorRef.current points to a destroyed instance whose
    // getValue() returns "" — causing "Script must not be empty" in chat mode.
    editor.onDidDispose(() => {
      editorRef.current = null;
    });
  };

  // "View Code" dot
  const [codeModifiedSinceViewed, setCodeModifiedSinceViewed] = useState(false);

  // ── Mode-switch side effects ───────────────────────────────
  // Clear the "View Code" dot when returning to Code mode
  useEffect(() => {
    if (editorMode === "code") {
      setCodeModifiedSinceViewed(false);
    }
  }, [editorMode]);

  // ── AI-injection flash on the Monaco container ─────────────
  // Bumped every time handleInjectCode runs (Marie wrote new code into the
  // editor). The effect below flips `editorFlashing` true for ~600 ms so a
  // single inset glow pulse plays on the editor container, pulling the eye
  // to where the action just happened. Code mode only — Chat mode has the
  // ghost-code-streaming-in-bubble behavior already.
  const [editorFlashKey, setEditorFlashKey] = useState(0);
  const [editorFlashing, setEditorFlashing] = useState(false);
  useEffect(() => {
    if (editorFlashKey === 0) return;
    setEditorFlashing(true);
    const t = setTimeout(() => setEditorFlashing(false), 600);
    return () => clearTimeout(t);
  }, [editorFlashKey]);

  // ── Modal states ───────────────────────────────────────────
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSaveAsModal, setShowSaveAsModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [metadataAnchor, setMetadataAnchor] = useState<HTMLButtonElement | null>(null);
  const metadataPopoverActionRef = useRef<{ updatePosition: () => void } | null>(null);

  // ── Derived ────────────────────────────────────────────────
  const panelWidth = panelCollapsed ? PANEL_COLLAPSED_WIDTH : PANEL_WIDTH;
  const workspaceWidth = containerWidth - panelWidth;

  // ── Handlers ──────────────────────────────────────────────
  function handleCopyCode() {
    navigator.clipboard.writeText(code).catch(() => {/* ignore */});
  }

  function handleDownload() {
    const blob = new Blob([code], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = buildDownloadFilename(loadedScriptTitle);
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleCodeStreamed(streamed: string) {
    setCode(streamed);
    onCodeChange?.(streamed);
    setCodeModifiedSinceViewed(true);
  }

  function handleInjectCode(rawCode: string) {
    const existingCode = editorRef.current?.getValue() ?? code;
    const versioned = buildVersionHeader(rawCode, existingCode, getLoggedInName());
    const channelLine = versioned.split("\n").find((l) => l.includes("create_channel")) ?? "(none)";
    // eslint-disable-next-line no-console
    console.log("[inject] called. len=", versioned.length, "editorRef=", editorRef.current ? "present" : "NULL", "mode=", editorMode, "channel=", channelLine);
    // Write into Monaco editor imperatively — or queue if the editor is unmounted (chat mode)
    if (editorRef.current) {
      editorRef.current.setValue(versioned);
    } else {
      // Editor is unmounted (chat mode) — queue for next mount
      pendingInjectionRef.current = versioned;
    }
    // Keep React state in sync
    setCode(versioned);
    onCodeChange?.(versioned);
    // If the user injected from Chat mode, mark that the editor has been updated
    if (editorMode === "chat") {
      setCodeModifiedSinceViewed(false); // they're already in Chat so no dot needed
    } else {
      // Injected from Code mode sidebar — no dot change needed, code is visible.
      // Pulse the editor container once to telegraph "code just landed here".
      // Chat mode already has the ghost-code-in-bubble streaming animation,
      // so we suppress the editor flash there to avoid competing cues.
      setEditorFlashKey((k) => k + 1);
    }
  }

  // Save click — entry point for both the sidebar Save button and the
  // collapsed-panel Save IconButton. Three branches:
  //   * No saved script (currentScriptId == null) → SaveScriptModal (first-save
  //     flow: title + description + category).
  //   * Saved script with no versions yet (defensive — versions list usually
  //     populates on script load) → fall through to overwrite-in-place so the
  //     user isn't blocked by a momentary loading state.
  //   * Saved script with versions → SaveVersionDialog so the user picks
  //     overwrite-head vs. create-new (with optional notes for create-new).
  function handleSaveClick() {
    if (currentScriptId === null) {
      setShowSaveAsModal(true);
      return;
    }
    if (versions.length === 0) {
      // Versions list hasn't loaded (or failed to load) — default to a
      // straight overwrite so the user can still save. Same path as the
      // dialog's overwrite branch.
      void handleOverwriteHead();
      return;
    }
    setShowSaveVersionDialog(true);
  }

  // Overwrite the current head version in place via PUT /api/scripts/:id.
  // Called by SaveVersionDialog's "Save changes to v{N}" branch and by the
  // versions-not-loaded fallback above.
  //
  // Auto-pair-on-Save (Greg, 2026-05-14): if the editor's selected image is a
  // library/user image AND its id differs from the script's known
  // default_image_id, include `default_image_id` in the payload and surface
  // the new pairing in the success Snackbar. Temp uploads and matching ids
  // omit the key (three-state PUT semantics — see UserScriptUpdatePayload
  // docs in api/client.ts).
  async function handleOverwriteHead() {
    if (currentScriptId === null) return;
    const currentCode = editorRef.current?.getValue() ?? code;
    const selectedId = selectedLibraryImageId();
    const pairingChanged =
      selectedId !== null && selectedId !== currentScriptDefaultImageId;
    const pairedImageName = pairingChanged ? selectedImage?.name ?? null : null;

    setSavingVersion(true);
    try {
      const payload: { code: string; marie_session_id: number | null; default_image_id?: number } = {
        code: currentCode,
        marie_session_id: marieSessionId ?? null,
      };
      if (pairingChanged) {
        payload.default_image_id = selectedId;
      }
      const updated = await userScriptsApi.update(currentScriptId, payload);
      // Mirror the server-side default_image_id back onto local state so
      // subsequent saves compare against the authoritative value (handles
      // the case where the response differs from what we sent — e.g. the
      // backend cleared the id due to a validation rule).
      setCurrentScriptDefaultImageId(updated.default_image_id ?? null);
      // Reset to "viewing head" — saving while pinned on a non-head view
      // promotes that view's content into the head, so we should snap back.
      setViewingVersionId(null);
      setVersionsRefreshKey((k) => k + 1);
      setShowSaveVersionDialog(false);
      const baseMessage = headVersion
        ? `Saved changes to v${headVersion.version_number}.`
        : "Script saved.";
      const pairingSuffix = pairingChanged
        ? ` Paired with ${pairedImageName ?? `image #${selectedId}`}.`
        : "";
      setSaveSnackbar({
        open: true,
        message: `${baseMessage}${pairingSuffix}`,
        severity: "success",
      });
    } catch (err) {
      setSaveSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Failed to save.",
        severity: "error",
      });
    } finally {
      setSavingVersion(false);
    }
  }

  // Create a new version row via POST /api/scripts/:id/versions. Backend
  // sets it as head and refreshes user_scripts.code. Called by
  // SaveVersionDialog's "Create new v{N+1}" branch. Notes are optional.
  //
  // Auto-pair-on-Save (Greg, 2026-05-14): the version create endpoint takes
  // only `code` + `notes` (image lives on the parent user_scripts row, not
  // on individual version snapshots). So when the pairing has changed, fire
  // a follow-up PUT against the parent script to update default_image_id.
  // Two trips, but it's the cleanest path without a backend change — the
  // alternative (extending ScriptVersionCreatePayload) is Sam's domain.
  async function handleCreateNewVersion(notes: string) {
    if (currentScriptId === null) return;
    const currentCode = editorRef.current?.getValue() ?? code;
    const selectedId = selectedLibraryImageId();
    const pairingChanged =
      selectedId !== null && selectedId !== currentScriptDefaultImageId;
    const pairedImageName = pairingChanged ? selectedImage?.name ?? null : null;

    setSavingVersion(true);
    try {
      const result = await userScriptsApi.createVersion(currentScriptId, {
        code: currentCode,
        notes: notes.trim() ? notes.trim() : null,
      });
      // Mirror authoritative state from the version-create response. The
      // returned `script` is the parent user_scripts row — its
      // default_image_id still reflects whatever pairing existed before
      // this save (the create endpoint doesn't touch it).
      setCurrentScriptDefaultImageId(result.script.default_image_id ?? null);

      // Follow-up image pairing PUT — only when the selected image changed.
      // The version-create endpoint stays code-only by design (versions
      // don't carry image metadata), so this two-step is still needed.
      // Failures here don't undo the version row; we surface them as a
      // visible error Snackbar so the user knows pairing didn't take.
      let pairingFailed = false;
      let pairingErrorMessage: string | null = null;
      if (pairingChanged) {
        try {
          const updated = await userScriptsApi.update(currentScriptId, {
            default_image_id: selectedId,
          });
          setCurrentScriptDefaultImageId(updated.default_image_id ?? null);
        } catch (pairErr) {
          pairingFailed = true;
          pairingErrorMessage =
            pairErr instanceof Error ? pairErr.message : null;
        }
      }
      setViewingVersionId(null);
      setVersionsRefreshKey((k) => k + 1);
      setShowSaveVersionDialog(false);
      const versionNumber = result.version.version_number;
      if (pairingFailed) {
        // The version itself succeeded — be explicit about the partial
        // outcome so the user doesn't think the whole save failed.
        const detail = pairingErrorMessage ? ` (${pairingErrorMessage})` : "";
        setSaveSnackbar({
          open: true,
          message: `Saved v${versionNumber}, but failed to update the paired image. Try the Edit pencil.${detail}`,
          severity: "error",
        });
      } else {
        const pairingSuffix = pairingChanged
          ? ` Paired with ${pairedImageName ?? `image #${selectedId}`}.`
          : "";
        setSaveSnackbar({
          open: true,
          message: `Created v${versionNumber}.${pairingSuffix}`,
          severity: "success",
        });
      }
    } catch (err) {
      setSaveSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Failed to save.",
        severity: "error",
      });
    } finally {
      setSavingVersion(false);
    }
  }

  // Pick a version from the dropdown — fetches the selected row's code
  // and loads it into the editor. Picking the head version is a no-op
  // (we just close the menu and clear viewingVersionId).
  async function handlePickVersion(version: ScriptVersion) {
    setVersionMenuAnchor(null);
    if (currentScriptId === null) return;
    if (headVersion && version.id === headVersion.id) {
      // Returning to head — no fetch needed, the editor's content already
      // mirrors the head row (or the user's in-progress edits to it).
      // NOTE: this overwrites unsaved edits to the head with… the head.
      // Since unsaved-edits-to-head still match the editor, it's a no-op.
      setViewingVersionId(null);
      return;
    }
    try {
      const row = await userScriptsApi.getVersion(currentScriptId, version.version_number);
      const incomingCode = row.code ?? "";
      // Mirror the existing handleInjectCode path so Monaco + local state +
      // parent stay in sync. We deliberately do NOT add a version-header
      // banner like AI injection does — restoring v2 should look like v2.
      if (editorRef.current) {
        editorRef.current.setValue(incomingCode);
      } else {
        pendingInjectionRef.current = incomingCode;
      }
      setCode(incomingCode);
      onCodeChange?.(incomingCode);
      setViewingVersionId(version.id);
      // Refresh parameter panel from the loaded version's # Default parameters block.
      const parsed = parseDefaultParameters(incomingCode);
      if (parsed && parsed.length > 0) {
        paramIdCounter.current = parsed.length;
        setParamPairs(parsed);
        setShowParamPanel(true);
      } else {
        setParamPairs([]);
        setShowParamPanel(false);
      }
    } catch (err) {
      setSaveSnackbar({
        open: true,
        message: err instanceof Error ? err.message : `Failed to load v${version.version_number}.`,
        severity: "error",
      });
    }
  }

  // Restore a non-head version as the new current. Keeps history intact —
  // the prior head row stays in script_versions, just no longer pointed at.
  async function handleConfirmRestore() {
    if (currentScriptId === null || pendingRestore === null) return;
    setRestoring(true);
    try {
      const updated = await userScriptsApi.update(currentScriptId, {
        head_version_id: pendingRestore.id,
      });
      // Mirror authoritative default_image_id from the response so the next
      // Save's auto-pair comparison stays accurate. Restoring a version
      // does not change the parent's default_image_id (versions don't carry
      // images) — this is mostly a defensive resync.
      setCurrentScriptDefaultImageId(updated.default_image_id ?? null);
      // Reflect the new head in the editor: backend has refreshed
      // user_scripts.code to match the restored version. Use updated.code
      // rather than re-fetching the version row.
      const newCode = updated.code ?? "";
      if (editorRef.current) {
        editorRef.current.setValue(newCode);
      } else {
        pendingInjectionRef.current = newCode;
      }
      setCode(newCode);
      onCodeChange?.(newCode);
      setViewingVersionId(null);
      setVersionsRefreshKey((k) => k + 1);
      setSaveSnackbar({
        open: true,
        message: `Restored v${pendingRestore.version_number} as current.`,
        severity: "success",
      });
      setPendingRestore(null);
    } catch (err) {
      setSaveSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Failed to restore version.",
        severity: "error",
      });
    } finally {
      setRestoring(false);
    }
  }

  async function handleSaveAs(data: { name: string; description: string; category: string }) {
    const currentCode = editorRef.current?.getValue() ?? code;
    // Auto-pair-on-Save As (Greg, 2026-05-14): pairing is implicit on a new
    // script, so we do NOT surface it in the Snackbar (spec). Sam shipped
    // `default_image_id` support on `POST /api/scripts` 2026-05-14, so this
    // is now a single atomic call — validation (422/403) runs before any
    // script row is inserted, and the create response carries the
    // server-truth pairing. Pairing failures surface through the top-level
    // try/catch (same path as any other create failure).
    const selectedId = selectedLibraryImageId();
    try {
      const created = await userScriptsApi.create({
        title: data.name,
        description: data.description || null,
        code: currentCode,
        is_public: false,
        marie_session_id: marieSessionId ?? null,
        // Only include the key when we have an id — omitting it produces a
        // null pairing server-side (same outcome as an explicit null on
        // create). `undefined` is dropped by `JSON.stringify`.
        ...(selectedId !== null ? { default_image_id: selectedId } : {}),
      });
      setCurrentScriptId(created.id);
      // Mirror authoritative pairing from the create response.
      setCurrentScriptDefaultImageId(created.default_image_id ?? null);
      setShowSaveAsModal(false);
      setSaveSnackbar({ open: true, message: "Script saved as new.", severity: "success" });
    } catch (err) {
      setSaveSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Failed to save script.",
        severity: "error",
      });
    }
  }

  function handleViewCode() {
    onEditorModeChange("code");
  }

  function handleModeToggle(_: React.MouseEvent<HTMLElement>, newMode: EditorMode | null) {
    if (newMode !== null) {
      onEditorModeChange(newMode);
    }
  }

  // ── Left Panel ─────────────────────────────────────────────
  function renderExpandedPanel() {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        {/* Panel meta-control: collapse chevron — full-width row */}
        <Box
          component="button"
          onClick={() => setPanelCollapsed(true)}
          aria-label="Collapse left panel"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            width: "100%",
            px: 1,
            py: 0.75,
            border: "none",
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "transparent",
            cursor: "pointer",
            flexShrink: 0,
            color: "text.secondary",
            "&:hover": { bgcolor: "action.hover" },
            transition: "background-color 150ms",
          }}
        >
          <Typography variant="caption" sx={{ fontSize: "0.6875rem", userSelect: "none" }}>
            Collapse
          </Typography>
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        </Box>

        {/* Section 2 — Script controls */}
        <Box sx={{ px: 1, pt: 1, pb: 0.5, display: "flex", flexDirection: "column", gap: 0.75, flexShrink: 0 }}>
          <Button
            fullWidth
            variant="contained"
            size="small"
            startIcon={
              isRunning ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 15 }} />
              )
            }
            disableElevation
            disabled={isRunning}
            onClick={() => {
              const scriptCode = editorRef.current?.getValue() ?? code;
              const imageId = selectedImage ? parseInt(selectedImage.id, 10) : undefined;
              onRun?.(scriptCode, imageId, tempImageFile ?? undefined, buildParamString());
            }}
            sx={{ justifyContent: "flex-start", fontWeight: 600 }}
          >
            {isRunning ? "Running…" : "Run"}
          </Button>
          {/* Save — single full-width outlined button.
              For SAVED scripts: opens SaveVersionDialog so the user picks
              overwrite-head vs. create-new-version (with optional notes for
              the latter). For NEW scripts: opens SaveScriptModal for the
              first-save naming flow. The split-button pattern lives on
              New Script below — this column splits versioning operations
              by direction: Save = SAVE direction (commit to disk), New
              Script = LOAD direction (pull a version into the editor). */}
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={
              savingVersion ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SaveOutlinedIcon sx={{ fontSize: 15 }} />
              )
            }
            onClick={handleSaveClick}
            disabled={isRunning || savingVersion}
            sx={{ justifyContent: "flex-start" }}
          >
            {savingVersion ? "Saving…" : "Save"}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<SaveAsOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => setShowSaveAsModal(true)}
            sx={{ justifyContent: "flex-start" }}
          >
            Save As
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={handleDownload}
            sx={{ justifyContent: "flex-start" }}
          >
            Download
          </Button>
          {/* New Script — split button.
              For SAVED scripts (currentScriptId != null && versions loaded):
              the left half starts a fresh script (parent handler resets
              workspace); the right chevron opens a Menu listing every
              version of the current script as a click-to-load row (mirrors
              the editor-toolbar dropdown — both surfaces share
              handlePickVersion so the "viewing not current" indicator +
              Restore stay consistent). For NEW/starter scripts: a single
              full-width "+ New Script" button. */}
          {currentScriptId !== null && versions.length > 0 ? (
            // Split button: left half = onNewScript, right chevron = LOAD
            // version menu. Assembled from two outlined <Button>s in a flex
            // Box (rather than MUI ButtonGroup) so the left half keeps
            // standard small-Button padding and matches the surrounding
            // outlined buttons in this column — ButtonGroup strips the
            // merged-border padding and pulls startIcon flush against the
            // label. Inner corners are square + the seam is removed via
            // borderRight: none on the left button.
            <Box sx={{ display: "flex", width: "100%" }} aria-label="New script">
              <Button
                variant="outlined"
                size="small"
                disabled={isRunning}
                onClick={() => onNewScript?.()}
                startIcon={<AddOutlinedIcon sx={{ fontSize: 15 }} />}
                sx={{
                  flex: 1,
                  justifyContent: "flex-start",
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  borderRight: "none",
                  // Keep the right edge from touching the chevron's border on
                  // hover (when borderRight would return) — preserves the seam.
                  "&:hover": { borderRight: "none" },
                }}
              >
                New Script
              </Button>
              <Tooltip title="Load a saved version" arrow>
                <Button
                  variant="outlined"
                  size="small"
                  aria-label="Load script version"
                  aria-haspopup="menu"
                  aria-expanded={Boolean(newScriptMenuAnchor)}
                  disabled={isRunning || versionsLoading}
                  onClick={(e) => setNewScriptMenuAnchor(e.currentTarget)}
                  endIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    flex: "0 0 auto",
                    px: 1,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    "& .MuiButton-endIcon": { ml: 0.25 },
                  }}
                >
                  Load
                </Button>
              </Tooltip>
            </Box>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<AddOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={() => onNewScript?.()}
              sx={{ justifyContent: "flex-start" }}
            >
              New Script
            </Button>
          )}
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<TuneOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => setShowParamPanel((v) => !v)}
            sx={{ justifyContent: "flex-start" }}
          >
            Parameters{paramPairs.filter((p) => p.key.trim()).length > 0 ? ` (${paramPairs.filter((p) => p.key.trim()).length})` : ""}
          </Button>
        </Box>

        {/* ── Script Parameters panel ─────────────────────────── */}
        {showParamPanel && (
          <Box sx={{ px: 1, pt: 1, pb: 1 }}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 0.75 }}>
                <TuneOutlinedIcon sx={{ fontSize: 13, color: "text.secondary", mr: 0.5 }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.6875rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Parameters
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Add parameter">
                  <IconButton size="small" onClick={addParamPair} sx={{ p: 0.25 }}>
                    <AddOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              {paramPairs.length === 0 ? (
                <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.6875rem" }}>
                  No parameters. Click + to add a key=value pair.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {paramPairs.map((pair) => (
                    <Box key={pair.id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <TextField
                        size="small"
                        placeholder="key"
                        value={pair.key}
                        onChange={(e) => updateParamPair(pair.id, "key", e.target.value)}
                        sx={{
                          flex: 1,
                          "& .MuiInputBase-input": { fontSize: "0.6875rem", py: 0.375, px: 0.75 },
                        }}
                      />
                      <Typography variant="caption" sx={{ color: "text.disabled", mx: 0.25 }}>=</Typography>
                      <TextField
                        size="small"
                        placeholder="value"
                        value={pair.value}
                        onChange={(e) => updateParamPair(pair.id, "value", e.target.value)}
                        sx={{
                          flex: 1,
                          "& .MuiInputBase-input": { fontSize: "0.6875rem", py: 0.375, px: 0.75 },
                        }}
                      />
                      <IconButton size="small" onClick={() => removeParamPair(pair.id)} sx={{ p: 0.25 }}>
                        <ClearOutlinedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                  {paramPairs.some((p) => p.key.trim()) && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.disabled",
                        fontSize: "0.625rem",
                        fontFamily: "monospace",
                        wordBreak: "break-all",
                        mt: 0.25,
                      }}
                    >
                      {buildParamString()}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Flex spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Code|Chat mode toggle — framed card just above the image cluster */}
        <Box
          sx={{
            mx: 1,
            mb: 4,
            p: 0.75,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.default",
            flexShrink: 0,
          }}
        >
          <ToggleButtonGroup
            value={editorMode}
            exclusive
            onChange={handleModeToggle}
            aria-label="Editor display mode"
            fullWidth
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                py: 0.5,
                fontSize: "0.75rem",
                fontWeight: 500,
                textTransform: "none",
                gap: 0.5,
                color: "text.secondary",
                borderColor: "divider",
                "&.Mui-selected": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(79,195,247,0.18)"
                      : "rgba(0,122,204,0.14)",
                  color: "primary.main",
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(79,195,247,0.24)"
                        : "rgba(0,122,204,0.20)",
                  },
                },
              },
            }}
          >
            <Tooltip title="Switch to Code">
              <ToggleButton value="code" aria-label="Code mode">
                <Badge
                  variant="dot"
                  color="primary"
                  invisible={!codeModifiedSinceViewed}
                  sx={{
                    "& .MuiBadge-dot": { width: 8, height: 8, borderRadius: "50%" },
                    "& .MuiBadge-badge": { top: 2, right: -4 },
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <CodeOutlinedIcon sx={{ fontSize: 16 }} />
                  Code
                </Badge>
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Switch to Chat">
              <ToggleButton value="chat" aria-label="Chat mode">
                <ChatOutlinedIcon sx={{ fontSize: 16 }} />
                Chat
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>

        <Divider />

        {/* Section 3 — Image controls + thumbnail (bottom-anchored) */}
        <Box sx={{ px: 1, pt: 0.75, pb: 0, display: "flex", flexDirection: "column", gap: 0.75, flexShrink: 0 }}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<CollectionsOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => setShowLibraryModal(true)}
            sx={{ justifyContent: "flex-start" }}
          >
            Select from Library
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<UploadOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => tempFileInputRef.current?.click()}
            sx={{ justifyContent: "flex-start" }}
          >
            Upload temp image
          </Button>
          <input
            ref={tempFileInputRef}
            type="file"
            accept=".tif,.tiff,.jpg,.jpeg,.png"
            hidden
            onChange={handleTempFileSelect}
          />
          <CloudUploadConfirmDialog
            open={pendingTempFile !== null}
            onCancel={cancelTempFile}
            onConfirm={confirmTempFile}
          />
        </Box>

        {/* Image thumbnail — anchored at very bottom */}
        <Box sx={{ p: 1, flexShrink: 0 }}>
          {(selectedImage || tempImageFile) ? (
            // Selected: thumbnail with overlaid Clear / Info / View buttons
            <Box sx={{ position: "relative", width: "100%", height: 140, borderRadius: "8px", overflow: "hidden" }}>
              {selectedImageBlobUrl ? (
                <Box component="img" src={selectedImageBlobUrl} alt={selectedImage?.name ?? tempImageFile?.name}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <Box sx={{ width: "100%", height: "100%", bgcolor: "action.selected",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CircularProgress size={24} sx={{ color: "primary.main", opacity: 0.6 }} />
                </Box>
              )}
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  px: 0.75,
                  py: 0.25,
                  fontSize: "0.625rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedImage?.name ?? tempImageFile?.name}
              </Typography>
              {/* Overlay action buttons */}
              <Box
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  display: "flex",
                  gap: 0.25,
                }}
              >
                <Tooltip title="Clear selection">
                  <IconButton
                    size="small"
                    onClick={clearImage}
                    aria-label="Clear image selection"
                    sx={{
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                      width: 24,
                      height: 24,
                      "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                    }}
                  >
                    <ClearOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                {selectedImage && (
                  <Tooltip title="Image metadata">
                    <IconButton
                      size="small"
                      onClick={(e) => setMetadataAnchor(e.currentTarget)}
                      aria-label="View image acquisition metadata"
                      sx={{
                        bgcolor: "rgba(0,0,0,0.55)",
                        color: "#fff",
                        width: 24,
                        height: 24,
                        "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="View full size">
                  <IconButton
                    size="small"
                    onClick={() => setShowImageModal(true)}
                    aria-label="View image full size"
                    sx={{
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                      width: 24,
                      height: 24,
                      "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                    }}
                  >
                    <ZoomInOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ) : (
            // No image: dashed placeholder — flashes orange when Run is clicked without an image
            <Box
              onClick={() => setShowLibraryModal(true)}
              sx={{
                width: "100%",
                height: 140,
                border: "1.5px dashed",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                cursor: "pointer",
                transition: "border-color 200ms, box-shadow 200ms",
                borderColor: imagePlaceholderFlashing ? "warning.main" : "divider",
                boxShadow: imagePlaceholderFlashing
                  ? "0 0 0 3px rgba(255,152,0,0.35)"
                  : "none",
                "@keyframes imageFlashPulse": {
                  "0%, 100%": { boxShadow: "0 0 0 3px rgba(255,152,0,0.35)" },
                  "50%": { boxShadow: "0 0 0 6px rgba(255,152,0,0.12)" },
                },
                animation: imagePlaceholderFlashing
                  ? "imageFlashPulse 0.6s ease-in-out 3"
                  : "none",
                "&:hover": { borderColor: "primary.main" },
              }}
            >
              <ImageOutlinedIcon
                sx={{
                  fontSize: 32,
                  color: imagePlaceholderFlashing ? "warning.main" : "text.disabled",
                  transition: "color 200ms",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.6875rem",
                  color: imagePlaceholderFlashing ? "warning.main" : "text.disabled",
                  fontWeight: imagePlaceholderFlashing ? 600 : 400,
                  transition: "color 200ms",
                }}
              >
                {imagePlaceholderFlashing ? "Click to select an image" : "No image selected"}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  function renderCollapsedPanel() {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "100%",
          gap: 0.5,
          pb: 1,
        }}
      >
        {/* Expand chevron — full-width row at top */}
        <Box
          component="button"
          onClick={() => setPanelCollapsed(false)}
          aria-label="Expand left panel"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            py: 0.75,
            border: "none",
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "transparent",
            cursor: "pointer",
            flexShrink: 0,
            color: "text.secondary",
            "&:hover": { bgcolor: "action.hover" },
            transition: "background-color 150ms",
            mb: 0.5,
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </Box>

        <Tooltip title={isRunning ? "Running…" : "Run"} placement="right">
          <span>
            <IconButton
              size="small"
              aria-label="Run script"
              disabled={isRunning}
              onClick={() => {
                const scriptCode = editorRef.current?.getValue() ?? code;
                const imageId = selectedImage ? parseInt(selectedImage.id, 10) : undefined;
                onRun?.(scriptCode, imageId, tempImageFile ?? undefined, buildParamString());
              }}
              sx={{ width: 36, height: 36 }}
            >
              {isRunning ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Save" placement="right">
          <IconButton size="small" onClick={handleSaveClick} aria-label="Save script" sx={{ width: 36, height: 36 }}>
            <SaveOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Save As" placement="right">
          <IconButton
            size="small"
            onClick={() => setShowSaveAsModal(true)}
            aria-label="Save script as"
            sx={{ width: 36, height: 36 }}
          >
            <SaveAsOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download" placement="right">
          <IconButton
            size="small"
            onClick={handleDownload}
            aria-label="Download script"
            sx={{ width: 36, height: 36 }}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="New Script" placement="right">
          <span>
            <IconButton
              size="small"
              onClick={() => onNewScript?.()}
              disabled={isRunning}
              aria-label="New script"
              sx={{ width: 36, height: 36 }}
            >
              <AddOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Divider flexItem sx={{ my: 0.25 }} />

        {/* Spacer — pushes image controls to bottom */}
        <Box sx={{ flex: 1 }} />

        {/* Code|Chat mode toggle — stacked icons just above the image cluster */}
        <Tooltip title="Switch to Code" placement="right">
          <Badge
            variant="dot"
            color="primary"
            invisible={!codeModifiedSinceViewed}
            sx={{
              "& .MuiBadge-dot": { width: 8, height: 8, borderRadius: "50%" },
              "& .MuiBadge-badge": { top: 6, right: 6 },
            }}
          >
            <IconButton
              size="small"
              onClick={() => onEditorModeChange("code")}
              aria-label="Switch to code mode"
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: editorMode === "code" ? "action.selected" : "transparent",
                color: editorMode === "code" ? "primary.main" : "text.secondary",
              }}
            >
              <CodeOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Badge>
        </Tooltip>
        <Tooltip title="Switch to Chat" placement="right">
          <IconButton
            size="small"
            onClick={() => onEditorModeChange("chat")}
            aria-label="Switch to chat mode"
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: editorMode === "chat" ? "action.selected" : "transparent",
              color: editorMode === "chat" ? "primary.main" : "text.secondary",
            }}
          >
            <ChatOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Divider flexItem sx={{ my: 0.25 }} />

        <Tooltip title="Select from Library" placement="right">
          <IconButton size="small" onClick={() => setShowLibraryModal(true)} aria-label="Select from library" sx={{ width: 36, height: 36 }}>
            <CollectionsOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Upload New" placement="right">
          <IconButton size="small" onClick={() => setShowUploadModal(true)} aria-label="Upload new image" sx={{ width: 36, height: 36 }}>
            <UploadOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        {/* Image thumbnail — shown when an image is selected */}
        {(selectedImage || tempImageFile) && (
          <Tooltip
            placement="right"
            title={
              selectedImageBlobUrl ? (
                <Box sx={{ p: 0.75 }}>
                  <Box
                    component="img"
                    src={selectedImageBlobUrl}
                    alt={selectedImage?.name ?? tempImageFile?.name}
                    sx={{ display: "block", maxWidth: 220, maxHeight: 220, borderRadius: "4px", objectFit: "contain" }}
                  />
                  <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "text.secondary", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selectedImage?.name ?? tempImageFile?.name}
                  </Typography>
                </Box>
              ) : (selectedImage?.name ?? tempImageFile?.name ?? "Selected image")
            }
            componentsProps={{
              tooltip: {
                sx: selectedImageBlobUrl
                  ? { bgcolor: "background.paper", boxShadow: 6, p: 0, border: "1px solid", borderColor: "divider" }
                  : {},
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 36,
                height: 36,
                borderRadius: "4px",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "action.selected",
                mb: 0.5,
                cursor: "pointer",
                "&:hover .collapsed-zoom-overlay": { opacity: 1 },
              }}
            >
              {selectedImageBlobUrl ? (
                <Box
                  component="img"
                  src={selectedImageBlobUrl}
                  alt={selectedImage?.name ?? tempImageFile?.name}
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <CircularProgress size={16} color="inherit" sx={{ opacity: 0.6 }} />
              )}
              {/* Zoom overlay — appears on hover */}
              <Box
                className="collapsed-zoom-overlay"
                onClick={() => setShowImageModal(true)}
                sx={{
                  position: "absolute",
                  inset: 0,
                  bgcolor: "rgba(0,0,0,0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0,
                  transition: "opacity 150ms",
                }}
              >
                <ZoomInOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
              </Box>
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  }

  // ── Main render ────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>

      {/* ── Left Panel ──────────────────────────────────────── */}
      <Box
        sx={{
          width: panelWidth,
          minWidth: panelWidth,
          flexShrink: 0,
          borderRight: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          transition: "width 200ms cubic-bezier(0.4,0,0.2,1)",
          overflow: "hidden",
        }}
      >
        {/* Panel content */}
        {panelCollapsed ? renderCollapsedPanel() : renderExpandedPanel()}
      </Box>

      {/* ── Center + AI area + bottom Console ──────────────── */}
      {/* Vertical flex: editor/chat row on top, persistent ConsolePanel
          at the bottom. Mirrors the Output tab so the user sees identical
          stderr/stdout (and the same green/red status indicator) without
          having to leave the editor view. Auto-expand on failure makes
          Marie's auto-error analysis legible — the user sees what Marie
          sees the moment a run fails. */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden", minWidth: 0, minHeight: 0 }}>
        {editorMode === "chat" ? (
          // Chat mode: AI panel fills full remaining width
          <AiPanel
            containerWidth={workspaceWidth}
            enableSnap={false}
            fullWidth
            editorMode="chat"
            onViewCode={handleViewCode}
            onCodeStreamed={handleCodeStreamed}
            onInjectCode={handleInjectCode}
            onRestoreCode={handleInjectCode}
            messages={chatMessages}
            onMessagesChange={onChatMessagesChange}
            isRunning={isRunning}
            autoAnalyzeError={autoAnalyzeError}
            onAutoAnalyzeErrorConsumed={onAutoAnalyzeErrorConsumed}
            autoAnalyzeImageKey={imageAnalysisKey}
            autoAnalyzeImageIdentity={imageAnalysisIdentity}
            pendingPrompt={pendingPrompt}
            onPendingPromptConsumed={onPendingPromptConsumed}
            getEditorCode={() => editorRef.current?.getValue() ?? code}
            getSelectedImageId={() => selectedImage ? parseInt(selectedImage.id, 10) : null}
            getSelectedImageSource={() => selectedImage?.source ?? null}
            getSelectedImageName={() => selectedImage?.name ?? null}
            getTempImageFile={() => tempImageFile}
            onLoadScript={onLoadScript}
            marieSessionId={marieSessionId}
            onMarieSessionIdChange={onMarieSessionIdChange}
            newSessionArmKey={newSessionArmKey}
            onArmNewSession={onArmNewSession}
            loadedScriptId={currentScriptId}
            onClearWorkspace={onClearWorkspace}
            allowedModels={allowedModels}
            onTierLimitToast={onTierLimitToast}
            selectedModel={selectedMarieModel}
            onSelectedModelChange={onSelectedMarieModelChange}
            onRunScript={() => {
              const scriptCode = editorRef.current?.getValue() ?? code;
              const imageId = selectedImage ? parseInt(selectedImage.id, 10) : undefined;
              onRun?.(scriptCode, imageId, tempImageFile ?? undefined, buildParamString());
            }}
          />
        ) : (
          // Code mode: toolbar (Copy/Download only) + editor + AI sidebar
          <Box sx={{ flex: 1, display: "flex", overflow: "hidden", minWidth: 0 }}>
            {/* Center column: toolbar + editor */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
              {/* Toolbar — versioning + supplementary actions.
                  Versioning controls (dropdown + "viewing not current"
                  indicator + Restore button) are only rendered when a
                  saved script is loaded (currentScriptId != null) AND the
                  versions list has at least one row. They sit on the left;
                  Copy/Download stay anchored on the right. */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  flexShrink: 0,
                  minHeight: 36,
                }}
              >
                {currentScriptId !== null && versions.length > 0 && headVersion && (
                  <>
                    {/* Version dropdown — chip-style trigger that opens a
                        Menu listing every version DESC by number. The chip
                        label shows the currently-viewed version + its notes
                        (truncated), so the user always knows what they're
                        looking at without opening the menu. */}
                    <Tooltip title="Switch versions" arrow>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => setVersionMenuAnchor(e.currentTarget)}
                        startIcon={<HistoryOutlinedIcon sx={{ fontSize: 14 }} />}
                        endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          maxWidth: 360,
                          minWidth: 0,
                          py: 0.25,
                          px: 1,
                          borderColor: "divider",
                          color: "text.primary",
                          "& .MuiButton-endIcon": { ml: 0.25 },
                          "& .MuiButton-startIcon": { mr: 0.5 },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {viewingVersion ? (
                            <>
                              v{viewingVersion.version_number}
                              {isViewingHead ? " (current)" : ""}
                              {viewingVersion.notes ? ` — ${viewingVersion.notes}` : ""}
                            </>
                          ) : (
                            "Versions"
                          )}
                        </Box>
                      </Button>
                    </Tooltip>

                    {/* Published-version indicator chip — only rendered when
                        a published version exists. Subtle outlined chip with
                        a pin icon; tooltip explains what it means. */}
                    {publishedVersionId !== null && (() => {
                      const pub = versions.find((v) => v.id === publishedVersionId);
                      if (!pub) return null;
                      return (
                        <Tooltip
                          title={`Community sees v${pub.version_number} (pinned)`}
                          arrow
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={<PushPinOutlinedIcon sx={{ fontSize: 12 }} />}
                            label={`v${pub.version_number}`}
                            sx={{
                              height: 22,
                              fontSize: "0.6875rem",
                              borderRadius: "8px",
                              "& .MuiChip-icon": { ml: 0.5 },
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        </Tooltip>
                      );
                    })()}

                    {/* "Viewing not current" indicator + Restore button.
                        Only shown when the user has explicitly picked a
                        non-head version from the dropdown. The chip uses
                        warning color so it's noticeable without shouting;
                        the Restore button is right next to it for one-click
                        recovery. */}
                    {!isViewingHead && headVersion && viewingVersion && (
                      <>
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={`Viewing v${viewingVersion.version_number} — v${headVersion.version_number} is current`}
                          sx={{
                            height: 22,
                            fontSize: "0.6875rem",
                            borderRadius: "8px",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                        <Tooltip title={`Make v${viewingVersion.version_number} the current version`} arrow>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<RestoreOutlinedIcon sx={{ fontSize: 14 }} />}
                            onClick={() => setPendingRestore(viewingVersion)}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.75rem",
                              py: 0.25,
                              px: 0.75,
                              minWidth: 0,
                              "& .MuiButton-startIcon": { mr: 0.5 },
                            }}
                          >
                            Restore v{viewingVersion.version_number}
                          </Button>
                        </Tooltip>
                      </>
                    )}

                    {/* Loading spinner while versions list is in-flight. Fits
                        in the same row so the toolbar height doesn't jump. */}
                    {versionsLoading && (
                      <CircularProgress size={12} sx={{ ml: 0.5, color: "text.disabled" }} />
                    )}

                    {/* Vertical separator between versioning and copy/download */}
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
                  </>
                )}

                {/* Spacer pushes Copy/Download to the right edge */}
                <Box sx={{ flex: 1 }} />

                <Tooltip title="Copy code to clipboard">
                  <IconButton size="small" onClick={handleCopyCode} aria-label="Copy code">
                    <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download as .py file">
                  <IconButton size="small" onClick={handleDownload} aria-label="Download script">
                    <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Monaco Editor — wrapper carries the AI-injection flash glow.
                  The inset box-shadow pulse fires whenever Marie writes code
                  into the editor (handleInjectCode) so the user's eye is
                  pulled to where the action happened. Pure presentation —
                  Monaco itself is unchanged. */}
              <Box
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  position: "relative",
                  transition: "box-shadow 600ms cubic-bezier(0.2, 0, 0, 1)",
                  boxShadow: editorFlashing
                    ? (theme) => `inset 0 0 0 3px ${alpha(theme.palette.primary.main, 0.4)}`
                    : "inset 0 0 0 0 transparent",
                }}
              >
                <Editor
                  height="100%"
                  language="python"
                  theme={colorMode === "dark" ? "vs-dark" : "light"}
                  value={code}
                  onChange={(value) => {
                    const newCode = value ?? "";
                    setCode(newCode);
                    onCodeChange?.(newCode);
                  }}
                  onMount={handleEditorMount}
                  options={{
                    fontSize: 14,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    wordWrap: "on",
                    automaticLayout: true,
                  }}
                />
              </Box>
            </Box>

            {/* AI Panel sidebar */}
            <AiPanel
              containerWidth={workspaceWidth}
              enableSnap
              initialWidth={aiPanelWidth}
              onWidthChange={setAiPanelWidth}
              editorMode="code"
              onInjectCode={handleInjectCode}
              onRestoreCode={handleInjectCode}
              messages={chatMessages}
              onMessagesChange={onChatMessagesChange}
              isRunning={isRunning}
              autoAnalyzeError={autoAnalyzeError}
              onAutoAnalyzeErrorConsumed={onAutoAnalyzeErrorConsumed}
              autoAnalyzeImageKey={imageAnalysisKey}
              autoAnalyzeImageIdentity={imageAnalysisIdentity}
              pendingPrompt={pendingPrompt}
              onPendingPromptConsumed={onPendingPromptConsumed}
              getEditorCode={() => editorRef.current?.getValue() ?? code}
              getSelectedImageId={() => selectedImage ? parseInt(selectedImage.id, 10) : null}
              getSelectedImageSource={() => selectedImage?.source ?? null}
              getSelectedImageName={() => selectedImage?.name ?? null}
              getTempImageFile={() => tempImageFile}
              onLoadScript={onLoadScript}
              marieSessionId={marieSessionId}
              onMarieSessionIdChange={onMarieSessionIdChange}
              newSessionArmKey={newSessionArmKey}
              onArmNewSession={onArmNewSession}
              loadedScriptId={currentScriptId}
              onClearWorkspace={onClearWorkspace}
              allowedModels={allowedModels}
              onTierLimitToast={onTierLimitToast}
              selectedModel={selectedMarieModel}
              onSelectedModelChange={onSelectedMarieModelChange}
              onRunScript={() => {
                const scriptCode = editorRef.current?.getValue() ?? code;
                const imageId = selectedImage ? parseInt(selectedImage.id, 10) : undefined;
                onRun?.(scriptCode, imageId, tempImageFile ?? undefined, buildParamString());
              }}
            />
          </Box>
        )}
        </Box>
        {/* Persistent ConsolePanel — header always visible (status indicator
            never disappears), auto-expanded on failure so the error is
            in front of the user the moment Marie starts analysing it. */}
        <ConsolePanel result={lastResult ?? null} />
      </Box>

      {/* ── Modals ───────────────────────────────────────────── */}
      <LibrarySelectModal
        open={showLibraryModal}
        onClose={() => setShowLibraryModal(false)}
        onSelect={(img) => {
          setSelectedImage(img);
          // User-initiated library pick → ask Marie to describe it. Identity
          // keyed on source+id so re-picking the same image is deduped.
          triggerImageAnalysis(`lib:${img.source}:${img.id}`);
        }}
      />
      <SaveScriptModal
        open={showSaveAsModal}
        onClose={() => setShowSaveAsModal(false)}
        mode="saveAs"
        onSave={handleSaveAs}
        pairedImage={selectedImage}
        pairedImageIsTemp={Boolean(tempImageFile)}
        // Open the library picker on top of the save dialog. MUI's Dialog
        // stacks z-indexes so the picker lands above; the save modal stays
        // open underneath. When the user picks, parent's setSelectedImage
        // fires and the PairedImageRow re-renders with the new image. No
        // close/reopen choreography needed.
        onPickPairedImage={() => setShowLibraryModal(true)}
        onClearPairedImage={() => clearImage()}
      />
      <FullscreenImageModal
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        imageName={selectedImage?.name}
        blobUrl={selectedImageBlobUrl ?? undefined}
      />

      {/* ── Version picker menu ─────────────────────────────────
          Anchored to the dropdown chip in the toolbar. Lists every version
          DESC by version_number with the head row marked "(current)" and
          the published row marked with a pin icon. Notes are rendered
          inline as a secondary row beneath each version label. */}
      <Menu
        anchorEl={versionMenuAnchor}
        open={Boolean(versionMenuAnchor)}
        onClose={() => setVersionMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            minWidth: 320,
            maxWidth: 460,
            maxHeight: 360,
            mt: 0.5,
            borderRadius: "12px",
          },
        }}
      >
        {versions.length === 0 && (
          <MenuItem disabled>
            <ListItemText
              primary={versionsError ?? "No versions yet."}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </MenuItem>
        )}
        {versions.map((v) => {
          const isHead = headVersion !== null && v.id === headVersion.id;
          const isPublished = publishedVersionId !== null && v.id === publishedVersionId;
          const isViewing = viewingVersion !== null && v.id === viewingVersion.id;
          return (
            <MenuItem
              key={v.id}
              selected={isViewing}
              onClick={() => handlePickVersion(v)}
              sx={{
                py: 0.75,
                alignItems: "flex-start",
                gap: 1,
                "&.Mui-selected": { bgcolor: "action.selected" },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    v{v.version_number}
                  </Typography>
                  {isHead && (
                    <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 500 }}>
                      (current)
                    </Typography>
                  )}
                  {isPublished && (
                    <Tooltip title="Pinned for community" arrow>
                      <PushPinOutlinedIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                    </Tooltip>
                  )}
                </Box>
                {v.notes && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mt: 0.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {v.notes}
                  </Typography>
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ flexShrink: 0, fontSize: "0.6875rem", mt: 0.25 }}
              >
                {formatVersionDate(v.created_at)}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* ── Sidebar New Script chevron menu ────────────────────
          Anchored to the chevron half of the New Script split-button.
          LOAD-direction only: header caption + the same version list the
          editor-toolbar dropdown shows. Click a row to load that version
          into the editor. handlePickVersion is shared with the toolbar
          dropdown, so the toolbar's "viewing not current" indicator +
          Restore button stay in lockstep regardless of which surface was
          used to switch versions. The SAVE direction (overwrite-head vs.
          create-new-version) lives on the Save button via SaveVersionDialog
          — keeping load and save split by button is the mental model. */}
      <Menu
        anchorEl={newScriptMenuAnchor}
        open={Boolean(newScriptMenuAnchor)}
        onClose={() => setNewScriptMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxWidth: 420,
            maxHeight: 380,
            mt: 0.5,
            borderRadius: "12px",
          },
        }}
      >
        {/* Header caption — makes the LOAD intent explicit so this menu
            isn't confused with the Save dialog's create-new-version path. */}
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.6875rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontWeight: 600,
            }}
          >
            Load version
          </Typography>
        </Box>
        <Divider sx={{ mb: 0.5 }} />
        {versionsLoading && (
          <MenuItem disabled sx={{ justifyContent: "center", py: 1 }}>
            <CircularProgress size={14} />
          </MenuItem>
        )}
        {!versionsLoading && versions.length === 0 && (
          <MenuItem disabled>
            <ListItemText
              primary={versionsError ?? "No versions yet."}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </MenuItem>
        )}
        {!versionsLoading && versions.map((v) => {
          const isHead = headVersion !== null && v.id === headVersion.id;
          const isPublished = publishedVersionId !== null && v.id === publishedVersionId;
          const isViewing = viewingVersion !== null && v.id === viewingVersion.id;
          return (
            <MenuItem
              key={v.id}
              selected={isViewing}
              onClick={() => {
                setNewScriptMenuAnchor(null);
                handlePickVersion(v);
              }}
              sx={{
                py: 0.75,
                alignItems: "flex-start",
                gap: 1,
                "&.Mui-selected": { bgcolor: "action.selected" },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    v{v.version_number}
                  </Typography>
                  {isHead && (
                    <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 500 }}>
                      (current)
                    </Typography>
                  )}
                  {isPublished && (
                    <Tooltip title="Pinned for community" arrow>
                      <PushPinOutlinedIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                    </Tooltip>
                  )}
                </Box>
                {v.notes && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mt: 0.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {v.notes}
                  </Typography>
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ flexShrink: 0, fontSize: "0.6875rem", mt: 0.25 }}
              >
                {formatVersionDate(v.created_at)}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* ── Save dialog (radio-card chooser) ───────────────────
          Opened when Save is clicked on an already-saved script. Two
          outlined cards: "Save changes to v{N} (current)" overwrites the
          head row in place; "Create new v{N+1}" creates a new version row
          and exposes an inline notes field for the change description. */}
      <SaveVersionDialog
        open={showSaveVersionDialog}
        onClose={() => !savingVersion && setShowSaveVersionDialog(false)}
        currentVersionNumber={headVersion?.version_number ?? 1}
        nextVersionNumber={
          versions.length > 0
            ? Math.max(...versions.map((v) => v.version_number)) + 1
            : (headVersion?.version_number ?? 0) + 1
        }
        saving={savingVersion}
        onConfirm={({ mode, notes }) => {
          if (mode === "overwrite") void handleOverwriteHead();
          else void handleCreateNewVersion(notes);
        }}
      />

      {/* ── Restore-confirmation dialog ──────────────────────────
          Confirms before flipping head_version_id. Phrased to make clear
          that history is preserved (the prior current version stays in
          the dropdown), so the action feels safe and reversible. */}
      <Dialog
        open={pendingRestore !== null}
        onClose={() => !restoring && setPendingRestore(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Restore version?</DialogTitle>
        <DialogContent>
          {pendingRestore && headVersion && (
            <Typography variant="body2" color="text.secondary">
              Make <strong>v{pendingRestore.version_number}</strong> the current version?
              v{headVersion.version_number} will still be in your version history.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setPendingRestore(null)}
            disabled={restoring}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmRestore}
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {restoring ? "Restoring…" : "Restore"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Image metadata popover — opens above the (i) button so the
           raw-metadata block grows up away from the viewport bottom.
           popoverActionRef.updatePosition() is called by the panel
           whenever the raw section toggles, keeping the popover anchored. */}
      <Popover
        open={Boolean(metadataAnchor)}
        anchorEl={metadataAnchor}
        action={metadataPopoverActionRef as React.RefObject<{ updatePosition: () => void }>}
        onClose={() => setMetadataAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        marginThreshold={12}
        PaperProps={{ sx: { minWidth: 280, maxWidth: 360, maxHeight: "calc(100vh - 32px)", overflowY: "auto" } }}
      >
        {selectedImage && (
          <MetadataDetailsPanel
            metadata={selectedImage.acquisition_metadata}
            source={selectedImage.metadata_source}
            imageId={parseInt(selectedImage.id, 10)}
            imageName={selectedImage.name}
            imageDescription={selectedImage.description}
            imageTags={selectedImage.tags ?? null}
            onResize={() => metadataPopoverActionRef.current?.updatePosition()}
            onImageChanged={(updated) =>
              setSelectedImage((prev) =>
                prev
                  ? {
                      ...prev,
                      name: updated.name,
                      description: updated.description,
                      tags: updated.tags ?? null,
                      acquisition_metadata: updated.acquisition_metadata ?? null,
                      metadata_source: (updated.metadata_source ?? null) as PlaceholderImage["metadata_source"],
                    }
                  : prev,
              )
            }
          />
        )}
      </Popover>

      {/* ── Save feedback snackbar ───────────────────────────── */}
      <Snackbar
        open={saveSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setSaveSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSaveSnackbar((s) => ({ ...s, open: false }))}
          severity={saveSnackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {saveSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
